import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { createWebSocketClient } from "../http/http";
import { toast } from "react-toastify";
import { PackageRecord } from "../entities/PackageRecord";
import './DeployDetailPage.css';
import {DeployState, DeployStateExt} from "../enums/DeployState";
import PackageService from "../http/PackageService";
import {Button, Modal} from "antd";
import DateFormatUtils from "../utils/DateFormatUtils";

interface WebSocketMessage {
    id: number;
    type: string;
    message: string;
}

const DeployDetailPage: React.FC = () => {
    const [messages, setMessages] = useState<WebSocketMessage[]>([]);
    const [packageRecord, setPackageRecord] = useState<PackageRecord | null>(null);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = parseInt(searchParams.get("id") || '0');
    const messageLogRef = useRef<HTMLPreElement | null>(null);
    const [packageState, setPackageState] = useState<DeployState>(DeployState.PENDING);
    const [isModalVisible, setIsModalVisible] = useState(false);

    useEffect(() => {
        if (id === 0){
            return;
        }
        document.title = `构建记录`;
        PackageService.getPackageRecord({ id: id }).then((response) => {
            if (response.isFailed()) {
                return;
            }
            const data = response.data;
            if (!data) {
                toast.error("未找到构建记录");
                return;
            }
            setPackageRecord(data);
            setPackageState(DeployStateExt.fromString(data.packageState));
        });
    }, [id]);

    const createWebSocket = (url: string) => {
        const socket = createWebSocketClient(url);
        socket.onopen = () => {
            console.log("WebSocket connection opened");
        };
        socket.onmessage = (event) => {
            const message: WebSocketMessage = JSON.parse(event.data);
            if (message.id !== id) {
                return;
            }
            if (message.type === "DONE"){
                socket.close();
                setPackageRecord(JSON.parse(message.message));
                return;
            }
            setMessages((prevMessages) => {
                if (message.message.endsWith("\r") && prevMessages.length > 0 && prevMessages[prevMessages.length - 1].message.endsWith("\r")) {
                    prevMessages.pop();
                }
                return [...prevMessages, message];
            });
        };
        socket.onerror = (error) => {
            console.error("WebSocket error:", error);
        };
        socket.onclose = () => {
            console.log("WebSocket connection closed");
        };
        return socket;
    }

    useEffect(() => {
        if (packageRecord?.packageState !== DeployState[DeployState.STARTED]) {
            return;
        }
        const socket = createWebSocket("/ws/printLog/package");
        return () => {
            socket.close();
        };
    }, [packageRecord]);

    useLayoutEffect(() => {
        const currentRef = messageLogRef.current;
        if (currentRef) {
            const { scrollTop, scrollHeight, clientHeight } = currentRef;
            const isAtBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 1;
            if (isAtBottom) {
                currentRef.scrollTop = scrollHeight;
            }
        }
    }, [messages]);

    const showDeleteConfirm = () => {
        setIsModalVisible(true);
    };

    const confirmDelete = () => {
        if (!packageRecord){
            return;
        }
        PackageService.deletePackageRecord({ id: packageRecord.id }).then((response) => {
            if (response.isFailed()) {
                toast.error("删除失败");
                return;
            }
            toast.success("删除成功！");
            setIsModalVisible(false);
        });
    };

    const cancelModal = () => {
        setIsModalVisible(false);
    };

    return (
        <div className="deploy-detail-page">
            {packageRecord && (
                <div>
                    <div className="deploy-detail-header">
                        <p className="section-header">构建信息</p>
                        {
                            packageRecord.packageUrl && (
                                <Button type="default" danger onClick={showDeleteConfirm}>
                                    删除包
                                </Button>
                            )
                        }
                    </div>
                    <div className="package-info">
                    <div className="package-info-column">
                            <p className="text-block"><strong>AppKey：</strong> {packageRecord.appKey}</p>
                            <p className="text-block">
                                <strong>创建时间：</strong> {DateFormatUtils.format(packageRecord.createTime)}
                            </p>
                            <div className="text-block">
                                <strong>状态：</strong><p
                                style={{color: DeployStateExt.getStatusColor(packageState)}}>{DeployStateExt.getPackageStateText(packageState)}</p>
                            </div>
                        </div>
                        <div className="package-info-column">
                            <p className="text-block"><strong>包名：</strong> {packageRecord.packageName}</p>
                            <p className="text-block">
                                <strong>更新时间：</strong> {DateFormatUtils.format(packageRecord.updateTime)}
                            </p>
                        </div>
                    </div>
                    <p className="section-header">构建日志</p>
                    <pre className="message-log" ref={messageLogRef}>
                        {packageRecord?.log ? packageRecord.log : messages.map((msg, index) => (
                            <div key={index}>
                                {msg.message}
                            </div>
                        ))}
                    </pre>
                    <Modal
                        title="确认删除"
                        open={isModalVisible}
                        onOk={confirmDelete}
                        onCancel={cancelModal}
                        okText="确认"
                        cancelText="取消"
                    >
                        <p>你确定要删除这个构建记录对应的包吗？</p>
                    </Modal>
                </div>
            )}
        </div>
    );
};

export default DeployDetailPage;